import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSignOutAlt,
  faCog,
  faMessage,
  faHourglass,
  faList,
  faBars,
  faClock,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import { Navbar as BootstrapNavbar,Tabs, Tab, Navbar as BsNavbar, Nav, Container } from 'react-bootstrap';
// Remover a importação de handleLogout de App.js
import 'bootstrap';
import '../../src/App.css';

import { useUserContext } from '../UserContext'; // Import the context
import { useEntidadeContext } from '../EntidadeContext'; // Importar contexto da entidade
import logoFxcImage from '../img/logofxc.png'; // Importa a imagem do logo

const AppNavbar = ({
  selectedModulo,
  onMudarPerfil,
  activeHeaderTabKey,
  setActiveHeaderTabKey,
  onLogout,
  userName,
  loginDateTime,
  permittedHeaderTabs = [], // Recebe as abas permitidas do App.js
  // entidadeNome, // Já obtemos via useEntidadeContext
  pacienteNome // Recebe o nome do paciente do App.js
}) => {
  const [expanded, setExpanded] = useState(false); // State for mobile menu collapse
  const navigate = useNavigate(); // Initialize useNavigate
  const { currentUserRole, dynamicAccessConfig, isLoadingAccessConfig } = useUserContext(); // Obter mais dados do contexto
  const { currentEntidade } = useEntidadeContext(); // Consumir o contexto da entidade

  // Verifica se o usuário pode ver o item de menu 'Comunicados'
  const canViewComunicados = useMemo(() => {
    if (isLoadingAccessConfig || !currentUserRole) return false;
    // Procura em qualquer módulo da API se existe uma configuração para 'comunicados'
    const moduleWithComunicados = Object.values(dynamicAccessConfig).find(
        module => module.comunicados && Array.isArray(module.comunicados.roles)
    );
    return moduleWithComunicados?.comunicados.roles.includes(currentUserRole) || false;
  }, [dynamicAccessConfig, currentUserRole, isLoadingAccessConfig]);

  // Verifica se o usuário pode ver o item de menu 'Configurações'
  const canViewConfiguracoes = useMemo(() => {
    if (isLoadingAccessConfig || !currentUserRole) return false;
    // Procura em qualquer módulo da API se existe uma configuração para 'configuracoes'
    const moduleWithConfig = Object.values(dynamicAccessConfig).find(
        module => module.configuracoes && Array.isArray(module.configuracoes.roles)
    );
    return moduleWithConfig?.configuracoes.roles.includes(currentUserRole) || false;
  }, [dynamicAccessConfig, currentUserRole, isLoadingAccessConfig]);

  const handleTabSelect = (key) => {
    setActiveHeaderTabKey(key);
    navigate(`/${key}`); // Navigate to the corresponding route
  };

  // Helper function to format date and time
  const formatDateTime = (date) => {
    if (!date) return { date: '--/--/----', time: '--:--' };
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return { date: `${day}/${month}/${year}`, time: `${hours}:${minutes}` };
  };
  const { date: formattedLoginDate, time: formattedLoginTime } = formatDateTime(loginDateTime);

  // Função para obter a classe de cor da navbar com base no perfil
  const getNavbarVariantClass = (role) => {
    switch (role) {
      case 'Representante Legal':
        return 'navbar-rl';
      case 'Médico':
        return 'navbar-medico';
      case 'Recepção':
        return 'navbar-recepcao';
      default:
        return 'bg-light'; // Cor padrão
    }
  };

  const navbarVariantClass = getNavbarVariantClass(currentUserRole);

  return (
    <BsNavbar // Estrutura simplificada para uma única Navbar
      className={`navbar-expand-lg navbar-light fixed-top custom-navbar-vibrant ${navbarVariantClass}`}
      expand="lg"
      expanded={expanded}
      onToggle={() => setExpanded(expanded ? false : "expanded")}
    >
      <Container fluid>
        {/* 1. Brand (Logo) - Visível apenas em desktop */}
        <Link to={permittedHeaderTabs.length > 0 ? `/${permittedHeaderTabs[0].key}` : "/"} className="navbar-brand d-none d-lg-block">
          <img
            src={logoFxcImage}
            alt="Logo"
            style={{ maxHeight: '40px' }} // Ajuste a altura conforme necessário
          />
        </Link>

        {/* 2. Informações do Usuário - Visível apenas em desktop, ao lado do logo */}
        <div className="navbar-text d-none d-lg-flex align-items-center me-auto">
              {currentEntidade?.nome || 'Nome da Clínica'} | {userName || 'Usuário'}{' '}
              <span className="mx-2">
                <FontAwesomeIcon icon={faCalendar} style={{ color: '#bab0b0' }} /> {formattedLoginDate}
              </span>
              <span>
                <FontAwesomeIcon icon={faClock} style={{ color: '#bab0b0' }} /> {formattedLoginTime}
              </span>
        </div>

        {/* Texto para a barra colapsada em mobile */}
        <BsNavbar.Text className="d-lg-none">Painel {currentUserRole}</BsNavbar.Text>

        {/* Mobile Menu Toggle */}
        <BsNavbar.Toggle
          aria-controls="responsive-navbar-nav"
        />

        <BsNavbar.Collapse id="responsive-navbar-nav">
          {/* Este container alinha os itens do menu à direita em telas de desktop */}
          <div className="ms-auto d-lg-flex align-items-center">
            {/* Itens do menu principal (Tabs) */}
            <Tabs 
              id="navbar-tabs"
              activeKey={activeHeaderTabKey}
              onSelect={handleTabSelect}
              className="mt-3 mt-lg-0" // Margem para mobile, removida em desktop
            >
              {permittedHeaderTabs
                .filter(tab => tab.key !== 'navitem')
                .map(tab => (
                  <Tab key={tab.key} eventKey={tab.key} title={tab.title} />
                ))}
            </Tabs>

            {/* Ações do usuário (dropdowns) */}
            <Nav className="flex-column flex-lg-row align-items-start align-items-lg-center mt-3 mt-lg-0 ms-lg-3">
                {/* Informações do usuário para a visualização móvel */}
                <Nav.Item className="d-lg-none text-muted p-2 border-bottom w-100 mb-2">
                  <div><strong>Clínica:</strong> {currentEntidade?.nome || 'N/A'}</div>
                  <div><strong>Usuário:</strong> {userName || 'N/A'}</div>
                </Nav.Item>

                {canViewComunicados && (
                  <Nav.Item as="li" className="nav-item dropdown">
                    <Nav.Link as="a" className="dropdown-toggle" href="#" id="navbarDropdownMessages" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      <FontAwesomeIcon icon={faMessage} /> <span className="d-lg-none">Mensagens</span>
                    </Nav.Link>
                    <div className="dropdown-menu" aria-labelledby="navbarDropdownMessages">
                      <Link className="dropdown-item" to="/comunicados">Comunicados</Link>
                    </div>
                  </Nav.Item>
                )}
                {canViewConfiguracoes && (
                  <Nav.Item as="li" className="nav-item dropdown">
                    <Nav.Link as="a" className="dropdown-toggle" href="#" id="navbarDropdownSettings" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      <FontAwesomeIcon icon={faCog} /> <span className="d-lg-none">Configurações</span>
                    </Nav.Link>
                    <div className="dropdown-menu" aria-labelledby="navbarDropdownSettings">
                      <Link className="dropdown-item" to="/configuracoes">Configurações</Link>
                    </div>
                  </Nav.Item>
                )}
                
                <Nav.Item as="li" className="nav-item dropdown">
                  <Nav.Link as="a" className="dropdown-toggle" href="#" id="navbarDropdownUser" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faUser} /> <span className="d-lg-none">Usuário</span>
                  </Nav.Link>

                  <div className="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownUser"> 
                    
                    {currentUserRole === 'Representante Legal' && (
                      <Link className="dropdown-item" to="/configuracoes" onClick={() => setExpanded(false)}>
                        Entidade
                      </Link>
                    )}
                    
                    <Link className="dropdown-item" to="/perfil" onClick={() => setExpanded(false)}>Perfil</Link>
                    <div className="dropdown-divider"></div>
                    <Link className="dropdown-item" onClick={onLogout}>Sair <FontAwesomeIcon icon={faSignOutAlt} /></Link>
                  </div>

                </Nav.Item>
            </Nav>
          </div>
        </BsNavbar.Collapse>
      </Container>
    </BsNavbar>
  );
};

export default AppNavbar;
